import React, { useEffect, useState } from "react";
import {
  ActionButton,
  DefaultButton,
  FontWeights,
  getId,
  IButtonStyles,
  IconButton,
  IIconProps,
  IStackProps,
  ITextFieldStyles,
  Label,
  MaskedTextField,
  mergeStyleSets,
  Modal,
  Spinner,
  Stack,
  StackItem,
  TextField,
  useTheme,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { extractErrorMessage, toErrorMessageBar } from "@helpers/alertUtils";
import { ICreateProductRequest } from "@models/dto/Rocketta/LicenseServer/Models/Products/ICreateProductRequest";
import { ProductApi } from "@api/productsApi";
import { IFeature } from "@models/interfaces";
import { IEditProductRequest } from "@models/dto/Rocketta/LicenseServer/Models/Products/IEditProductRequest";

const cancelIcon: IIconProps = { iconName: "Cancel" };
const deleteIcon: IIconProps = { iconName: "Delete" };
const stackProps: Partial<IStackProps> = {
  horizontal: false,
  tokens: { childrenGap: 10 },
  styles: { root: { marginBottom: 20 } },
};

export interface IProductModalProps {
  productModalVisible: boolean;
  hideProductModal: () => void;
  productId: number | undefined;
}

export const ProductModal: React.FC<IProductModalProps> = (props: IProductModalProps) => {
  const { productId, productModalVisible, hideProductModal } = props;
  const [features, setFeatures] = useState<IFeature[]>([]);
  const theme = useTheme();
  const titleId = useId("title");
  const [savingProduct, setSavingProduct] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [productName, setProductName] = useState<string>("");
  const [productUniqueId, setProductUniqueId] = useState<string>("");
  const [trialExpirationDays, setTrialExpirationDays] = useState<number>(0);
  const [expirationDays, setExpirationDays] = useState<number>(0);

  const addIcon: IIconProps = { iconName: "Add", color: theme.palette.neutralPrimary };
  const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
      color: theme.palette.neutralPrimary,
      marginLeft: "auto",
      marginTop: "4px",
      marginRight: "2px",
    },
    rootHovered: {
      color: theme.palette.neutralDark,
    },
  };

  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      width: "500px",
    },
    header: [
      theme.fonts.xLargePlus,
      {
        flex: "1 1 auto",
        borderTop: `4px solid ${theme.palette.themePrimary}`,
        color: theme.palette.neutralPrimary,
        display: "flex",
        alignItems: "center",
        fontWeight: FontWeights.semibold,
        padding: "12px 12px 14px 24px",
      },
    ],
    heading: {
      color: theme.palette.neutralPrimary,
      fontWeight: FontWeights.semibold,
      fontSize: "inherit",
      margin: "0",
    },
    body: {
      flex: "4 4 auto",
      padding: "0 24px 24px 24px",
      overflowY: "hidden",
      selectors: {
        p: { margin: "14px 0" },
        "p:first-child": { marginTop: 0 },
        "p:last-child": { marginBottom: 0 },
      },
    },
  });

  const numberTextFieldStyles: Partial<ITextFieldStyles> = {
    root: {
      width: "200px",
    },
  };

  useEffect(() => {
    if (productId) {
      ProductApi.getProduct(productId)
        .then((result) => {
          const productInfo = result.data.data;
          setProductName(productInfo.name);
          setProductUniqueId(productInfo.productId);
          setExpirationDays(productInfo.expirationDays);
          setTrialExpirationDays(productInfo.trialExpirationDays);
          setFeatures(
            productInfo.features.map((f) => ({
              id: f.id.toString(),
              name: f.name,
            }))
          );

          return result.data.data;
        })
        .catch((err) => {
          const message = extractErrorMessage(err);
          setErrorMessage(message);
        });
    }
  }, [productId]);

  const addNewFeature = (): void => {
    const newFeature: IFeature = { id: getId("newfeature"), name: "" };
    const newFeaturesUpd = [...features, newFeature];
    setFeatures(newFeaturesUpd);
  };

  const onNewFeatureChange = (f: IFeature, newValue: string): void => {
    const resultFeatures = [...features];
    for (let i = 0; i < resultFeatures.length; i++) {
      if (resultFeatures[i].id === f.id) {
        resultFeatures[i] = { id: f.id, name: newValue };
        setFeatures(resultFeatures);
        break;
      }
    }
  };

  const onDeleteNewFeature = (f: IFeature): void => {
    const resultFeatures = features.filter((feature) => feature.id !== f.id);
    setFeatures(resultFeatures);
  };

  const handleHideProductModal = () => {
    setProductUniqueId("");
    setProductName("");
    setFeatures([]);
    setExpirationDays(0);
    setTrialExpirationDays(0);
    setErrorMessage("");
    hideProductModal();
  };

  const onSaveClick = async (): Promise<void> => {
    try {
      setSavingProduct(true);
      if (productId > 0) {
        const editProductRequest: IEditProductRequest = {
          id: productId,
          name: productName,
          features: features.map((f) => ({ name: f.name, id: f.id.startsWith("newfeature") ? 0 : Number(f.id) })),
          trialExpirationDays: trialExpirationDays,
          expirationDays: expirationDays,
        };
        await ProductApi.editProduct(editProductRequest);
      } else {
        const createProductRequest: ICreateProductRequest = {
          productId: productUniqueId,
          name: productName,
          features: features.map((f) => ({ name: f.name })),
          trialExpirationDays: trialExpirationDays,
          expirationDays: expirationDays,
        };
        await ProductApi.createProduct(createProductRequest);
      }

      handleHideProductModal();
      window.location.reload();
    } catch (err) {
      const msg = extractErrorMessage(err);
      setErrorMessage(msg);
    } finally {
      setSavingProduct(false);
    }
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={productModalVisible}
      onDismiss={handleHideProductModal}
      isBlocking={true}
      containerClassName={contentStyles.container}
    >
      <div className={contentStyles.header}>
        <h2 className={contentStyles.heading} id={titleId}>
          {productId > 0 ? "Edit product" : "Create product"}
        </h2>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={handleHideProductModal}
        />
      </div>
      <div className={contentStyles.body}>
        <Stack {...stackProps}>
          <StackItem>
            <TextField
              label="Name"
              autoComplete="off"
              value={productName}
              onChange={(ev, newValue) => setProductName(newValue)}
            />
          </StackItem>
          <StackItem>
            <MaskedTextField
              disabled={productId > 0}
              placeholder="{00000000-0000-0000-0000-000000000000}"
              label="Id"
              value={productUniqueId}
              mask="{********-****-****-****-************}"
              onChange={(ev, newValue) => setProductUniqueId(newValue)}
            />
          </StackItem>
          <StackItem>
            <TextField
              styles={numberTextFieldStyles}
              autoComplete="off"
              label="Trial expiration days"
              type="number"
              value={trialExpirationDays.toString()}
              onChange={(ev, newValue) => setTrialExpirationDays(Number(newValue))}
            />
          </StackItem>
          <StackItem>
            <TextField
              styles={numberTextFieldStyles}
              autoComplete="off"
              label="Expiration days"
              type="number"
              value={expirationDays.toString()}
              onChange={(ev, newValue) => setExpirationDays(Number(newValue))}
            />
          </StackItem>
          <StackItem>
            <Stack {...stackProps}>
              <Stack horizontal tokens={stackProps.tokens}>
                <Label>Features</Label>
                <ActionButton
                  iconProps={addIcon}
                  size={50}
                  allowDisabledFocus
                  disabled={false}
                  checked={false}
                  onClick={addNewFeature}
                >
                  Add feature
                </ActionButton>
              </Stack>
              {features &&
                features.map((f) => (
                  <Stack horizontal key={f.id}>
                    <TextField
                      placeholder="Feature"
                      value={f.name}
                      onChange={(ev, newValue) => onNewFeatureChange(f, newValue)}
                    ></TextField>
                    <IconButton iconProps={deleteIcon} onClick={() => onDeleteNewFeature(f)} />
                  </Stack>
                ))}
            </Stack>
          </StackItem>
          <StackItem>
            <Stack horizontal horizontalAlign="end" verticalAlign="center" tokens={stackProps.tokens}>
              <DefaultButton onClick={onSaveClick}>Save</DefaultButton>
              {savingProduct && <Spinner />}
            </Stack>
          </StackItem>
          {errorMessage && <StackItem> {toErrorMessageBar(errorMessage)}</StackItem>}
        </Stack>
      </div>
    </Modal>
  );
};
